import styled from '@emotion/styled';
import {media, mediaBetween} from '@nfq/react-grid';
import {m as motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';

import {PayloadVideo} from 'UI/components/layout/PayloadVideo';

import {useFullsizeVideo} from './useFullsizeVideo';

import type {FullsizeVideoBlock} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the properties object expected by the `FullsizeVideo` component.
 * It includes optional video source data, headline content, a test identifier, and a theme attribute for styling control.
 * This interface leverages the `FullsizeVideoBlock` type from the Payload CMS schema to ensure type consistency between CMS data and the component.
 */
interface ComponentProps {
    /**
     * The optional background video object containing desktop and mobile video sources in primary and fallback formats.
     */
    bgVideo?: FullsizeVideoBlock['bgVideo'];
    /**
     * An optional unique identifier assigned to the component for testing purposes, enabling reliable element targeting in automated tests.
     *
     * @default 'FullsizeVideo'
     */
    testId?: string;
    /**
     * The optional theme data attribute applied to the component's container for switching between color schemes.
     */
    theme?: FullsizeVideoBlock['theme'];
}

/**
 * The `FullsizeVideo` component renders a full-width video section with scroll-driven scale animation and a parallax text overlay.
 * The video automatically plays when it enters the viewport and pauses when it leaves, creating an immersive scroll-based experience.
 * Headline text is animated horizontally with a spring-based parallax effect, rendered on top of the video using the primary theme color.
 *
 * @param props         The component props.
 * @param props.bgVideo The optional background video object containing desktop and mobile video sources.
 * @param props.testId  An optional unique identifier for targeting the component in automated tests, defaulting to `'FullsizeVideo'`.
 * @param props.theme   The optional theme data attribute for switching between color schemes on the component container.
 * @returns A JSX element representing a full-size video with scroll-driven scaling and animated headline text overlay.
 *
 * @example
 * ```tsx
 * <FullsizeVideo
 *   bgVideo={videoSources}
 *   testId="fullsize-video"
 *   theme="dark"
 * />
 * ```
 */
const FullsizeVideo = observer(({bgVideo, testId = 'FullsizeVideo', theme}: ComponentProps) => {
    const {
        pauseVideo,
        playVideo,
        scale,
        scrollRef,
        videoRef
    } = useFullsizeVideo();

    return (
        <VideoWrapper ref={scrollRef} data-cy={testId} data-nfq-theme={theme}>
            <StyledVideo
                ref={videoRef}
                style={{scale: scale as unknown as string}}
                video={bgVideo}
                width="100%"
                loop
                muted
                playsInline
                onViewportEnter={playVideo}
                onViewportLeave={pauseVideo}
            />
        </VideoWrapper>
    );
});

FullsizeVideo.displayName = 'FullsizeVideo';

export {FullsizeVideo};

const VideoWrapper = styled(motion.div)`
    line-height: 0;
    overflow: hidden;
    position: relative;
`;

const StyledVideo = styled(motion.create(PayloadVideo))`
    width: 100%;

    ${mediaBetween('xs', 'md')} {
        transform: none !important;
    }

    ${media('md')} {
        aspect-ratio: initial;
        height: auto;
        width: 100%;
    }
`;